import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatResidentLoginInfoSessionStorageHelper,
    ResidentLoginInfoSessionStorageKey,
} from '../../../common/authentication/NatResidentLoginInfoSessionStorageHelper'
import {NatApplicationRepo} from '../repository/NatApplicationRepo'
import {NatResidentLongAbsenceLocationState} from './NatResidentApplicationInterface'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import {PlainBackLink} from '../../../../styledComponents/Links'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {Box} from '@mui/material'
import {Nullable} from '../../../../app/TypeHelpers'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {ListGridContainer, PageTitle} from '../common/resident/NatResidentApplicationsCommon'
import {
    createResidentWithErrorHandlingPage,
    ResidentWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createResidentWithErrorHandlingPage'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import {Messages} from '../../../../Messages'
import {NatPutLongAbsenceRequest, NatPutLongAbsenceResponse} from '../repository/NatPutLongAbsence'
import {FontSize} from '../../../../styledComponents/Constants'
import NatResidentLongAbsenceForm from './resident/NatResidentLongAbsenceForm'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    applicationRepo: NatApplicationRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatResidentLongAbsenceLocationState>
}

interface State {
    applicantFamilyName: Nullable<string>
    applicantFirstName: Nullable<string>
    absenceStartDate: Nullable<string>
    absenceEndDate: Nullable<string>
    applicantContactPhoneNumber: Nullable<string>
    destinationType: Nullable<string>
    destinationZipCode: Nullable<string>
    destinationPrefecture: Nullable<string>
    destinationAddress2: Nullable<string>
    destinationAddress3: Nullable<string>
    destinationBuildingRoomNumber: Nullable<string>
    destinationAddress: Nullable<string>
    contactFamilyName: Nullable<string>
    contactFirstName: Nullable<string>
    contactRelationship: Nullable<string>
    contactZipCode: Nullable<string>
    contactPrefecture: Nullable<string>
    contactAddress2: Nullable<string>
    contactAddress3: Nullable<string>
    contactBuildingRoomNumber: Nullable<string>
    contactPhoneNumber: Nullable<string>
    isLoadCompleted: boolean
}

class NatResidentLongAbsenceInputPage
    extends React.Component<Props & ResidentWithErrorHandlingPagePropsSet, State> {

    constructor(props: Props & ResidentWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            applicantFamilyName: null,
            applicantFirstName: null,
            absenceStartDate: null,
            absenceEndDate: null,
            applicantContactPhoneNumber: null,
            destinationType: null,
            destinationZipCode: null,
            destinationPrefecture: null,
            destinationAddress2: null,
            destinationAddress3: null,
            destinationBuildingRoomNumber: null,
            destinationAddress: null,
            contactFamilyName: null,
            contactFirstName: null,
            contactRelationship: null,
            contactZipCode: null,
            contactPrefecture: null,
            contactAddress2: null,
            contactAddress3: null,
            contactBuildingRoomNumber: null,
            contactPhoneNumber: null,
            isLoadCompleted: false,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {applyId, condominiumCd, applicationRepo, eventEmitter} = this.props
        const applyInfo = this.props.location.state?.applyInfo

        const loginFamilyName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FAMILY_NAME)
        const loginFirstName = NatResidentLoginInfoSessionStorageHelper
            .getStringItemOrNull(ResidentLoginInfoSessionStorageKey.LOGIN_FIRST_NAME)

        const defaultApplicantFamilyName = applyInfo?.applicantFamilyName ?? loginFamilyName
        const defaultApplicantFirstName = applyInfo?.applicantFirstName ?? loginFirstName
        let defaultAbsenceStartDate = applyInfo?.absenceStartDate ?? null
        let defaultAbsenceEndDate = applyInfo?.absenceEndDate ?? null
        let defaultApplicantContactPhoneNumber = applyInfo?.applicantContactPhoneNumber ?? null
        let defaultDestinationType = applyInfo?.destinationType ?? null
        let defaultDestinationZipCode = applyInfo?.destinationZipCode ?? null
        let defaultDestinationPrefecture = applyInfo?.destinationPrefecture ?? null
        let defaultDestinationAddress2 = applyInfo?.destinationAddress2 ?? null
        let defaultDestinationAddress3 = applyInfo?.destinationAddress3 ?? null
        let defaultDestinationBuildingRoomNumber = applyInfo?.destinationBuildingRoomNumber ?? null
        let defaultDestinationAddress = applyInfo?.destinationAddress ?? null
        let defaultContactFamilyName = applyInfo?.contactFamilyName ?? null
        let defaultContactFirstName = applyInfo?.contactFirstName ?? null
        let defaultContactRelationship = applyInfo?.contactRelationship ?? null
        let defaultContactZipCode = applyInfo?.contactZipCode ?? null
        let defaultContactPrefecture = applyInfo?.contactPrefecture ?? null
        let defaultContactAddress2 = applyInfo?.contactAddress2 ?? null
        let defaultContactAddress3 = applyInfo?.contactAddress3 ?? null
        let defaultContactBuildingRoomNumber = applyInfo?.contactBuildingRoomNumber ?? null
        let defaultContactPhoneNumber = applyInfo?.contactPhoneNumber ?? null

        if (!applyInfo && applyId) {
            await this.props.eventEmitter.showLoading()
            try {
                const longAbsenceDetail = await this.props.asyncCallFunction(
                    applicationRepo.getLongAbsenceApplyForResident.bind(
                        applicationRepo, condominiumCd, applyId,
                    ),
                    (httpErrorOrNetworkError) => {
                        eventEmitter.showHeadingAreaMessage(httpErrorOrNetworkError)
                    },
                )
                if (longAbsenceDetail && longAbsenceDetail.details) {
                    defaultAbsenceStartDate = longAbsenceDetail.details.absenceStartDate
                    defaultAbsenceEndDate = longAbsenceDetail.details.absenceEndDate
                    defaultApplicantContactPhoneNumber = longAbsenceDetail.details.applicantContactPhoneNumber
                    defaultDestinationType = longAbsenceDetail.details.destinationType
                    defaultDestinationZipCode = longAbsenceDetail.details.destinationZipCode
                    defaultDestinationPrefecture = longAbsenceDetail.details.destinationPrefecture
                    defaultDestinationAddress2 = longAbsenceDetail.details.destinationAddress2
                    defaultDestinationAddress3 = longAbsenceDetail.details.destinationAddress3
                    defaultDestinationBuildingRoomNumber = longAbsenceDetail.details.destinationBuildingRoomNumber
                    defaultDestinationAddress = longAbsenceDetail.details.destinationAddress
                    defaultContactFamilyName = longAbsenceDetail.details.contactFamilyName
                    defaultContactFirstName = longAbsenceDetail.details.contactFirstName
                    defaultContactRelationship = longAbsenceDetail.details.contactRelationship
                    defaultContactZipCode = longAbsenceDetail.details.contactZipCode
                    defaultContactPrefecture = longAbsenceDetail.details.contactPrefecture
                    defaultContactAddress2 = longAbsenceDetail.details.contactAddress2
                    defaultContactAddress3 = longAbsenceDetail.details.contactAddress3
                    defaultContactBuildingRoomNumber = longAbsenceDetail.details.contactBuildingRoomNumber
                    defaultContactPhoneNumber = longAbsenceDetail.details.contactPhoneNumber
                }
            } finally {
                this.props.eventEmitter.closeLoading()
            }
        }

        this.setState({
            applicantFamilyName: defaultApplicantFamilyName,
            applicantFirstName: defaultApplicantFirstName,
            absenceStartDate: defaultAbsenceStartDate,
            absenceEndDate: defaultAbsenceEndDate,
            applicantContactPhoneNumber: defaultApplicantContactPhoneNumber,
            destinationType: defaultDestinationType,
            destinationZipCode: defaultDestinationZipCode,
            destinationPrefecture: defaultDestinationPrefecture,
            destinationAddress2: defaultDestinationAddress2,
            destinationAddress3: defaultDestinationAddress3,
            destinationBuildingRoomNumber: defaultDestinationBuildingRoomNumber,
            destinationAddress: defaultDestinationAddress,
            contactFamilyName: defaultContactFamilyName,
            contactFirstName: defaultContactFirstName,
            contactRelationship: defaultContactRelationship,
            contactZipCode: defaultContactZipCode,
            contactPrefecture: defaultContactPrefecture,
            contactAddress2: defaultContactAddress2,
            contactAddress3: defaultContactAddress3,
            contactBuildingRoomNumber: defaultContactBuildingRoomNumber,
            contactPhoneNumber: defaultContactPhoneNumber,
            isLoadCompleted: true,
        })
    }

    async onClickApplyConfirmButton(request: NatPutLongAbsenceRequest): Promise<void> {
        const {applicationRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutLongAbsenceResponse> = await this.props.asyncCallFunction(
                applicationRepo.applyLongAbsence.bind(
                    applicationRepo, condominiumCd, request),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                },
                {
                    defaultErrorTitle: Messages.EOPCR0014.toString(),
                    defaultErrorTitlePriority: true,
                    replacementDetailErrorMessages: [{
                        targetMessageId: Messages.EOPCA0004,
                        replacementMessageId: Messages.EOPCR0015,
                    }],
                },
            )

            if (response && response.requestError === null) {
                const locationState: NatResidentLongAbsenceLocationState = {
                    applyInfo: {
                        applicantFamilyName: request.applicantFamilyName,
                        applicantFirstName: request.applicantFirstName,
                        absenceStartDate: request.absenceStartDate,
                        absenceEndDate: request.absenceEndDate,
                        applicantContactPhoneNumber: request.applicantContactPhoneNumber,
                        destinationType: request.destinationType,
                        destinationZipCode: request.destinationZipCode,
                        destinationPrefecture: request.destinationPrefecture,
                        destinationAddress2: request.destinationAddress2,
                        destinationAddress3: request.destinationAddress3,
                        destinationBuildingRoomNumber: request.destinationBuildingRoomNumber,
                        destinationAddress: request.destinationAddress,
                        contactFamilyName: request.contactFamilyName,
                        contactFirstName: request.contactFirstName,
                        contactRelationship: request.contactRelationship,
                        contactZipCode: request.contactZipCode,
                        contactPrefecture: request.contactPrefecture,
                        contactAddress2: request.contactAddress2,
                        contactAddress3: request.contactAddress3,
                        contactBuildingRoomNumber: request.contactBuildingRoomNumber,
                        contactPhoneNumber: request.contactPhoneNumber,
                    },
                    applyId: this.props.applyId,
                }

                this.props.navigate(
                    `/resident/condominiums/${this.props.condominiumCd}/applications/longAbsence/confirm`,
                    {state: JSON.parse(JSON.stringify(locationState))},
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return (<></>)
        }

        return (
            <>
                <PlainBackLink
                    to={this.props.applyId
                        ? `/resident/condominiums/${this.props.condominiumCd}/applications/longAbsence/${this.props.applyId}`
                        : `/resident/condominiums/${this.props.condominiumCd}/applications`}>
                    戻る
                </PlainBackLink>
                <BlockSpacer height={'16px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    長期不在届
                </PageTitle>
                <Box height={'20px'}/>
                <Box>
                    １か月以上不在になる場合に提出が必要です。不在中に緊急事態が発生した際、管理組合または管理会社から連絡させていただくための連絡先を登録します。
                    <Box height={'10px'}/>
                    <ListGridContainer fontSize={FontSize.Middle}>
                        <div>・</div>
                        <div>
                            国外に滞在する場合は、国内連絡先として申請いただいた連絡先あてに通知します。
                        </div>
                        <div>・</div>
                        <div>
                            本届の提出がない場合は、専有部分の所在地あてに通知します。ただし、占有者に対しては、当該住戸を所有する組合員の連絡先とします。
                        </div>
                        <div>・</div>
                        <div>
                            また、本届の不備及び未提出に起因する事故等については、管理組合は責任を負いません。
                        </div>
                    </ListGridContainer>
                </Box>
                <Box height={'20px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatResidentLongAbsenceForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
            </>
        )
    }
}

export default createResidentWithErrorHandlingPage(NatResidentLongAbsenceInputPage)
